import type { SessionImage } from '@/api/image-contributions.api';

/**
 * Pure logic of the "Images" session: what the keyboard input becomes, the
 * answer sent for an image and the session figures. Behaviour is the one of
 * the former hooks/use-label-session.ts and pages/LabelImages.tsx.
 */

/** Images asked for each session. */
export const TOTAL_IMAGES = 10;

export type LabelSessionState = 'idle' | 'loading' | 'active' | 'completing' | 'completed' | 'error';

export type LabelAnswer = {
  imageIndex: number;
  userAnswer: string;
  expectedCharacter: string;
  isCorrect: boolean;
};

/**
 * The on-screen keyboard hands the whole typed value; only the last character
 * counts. The digraphs gb, kp and ny stay together.
 */
export function extractCharacter(input: string): string {
  const lower = input.toLowerCase();
  if (lower.endsWith('gb')) return input.slice(-2);
  if (lower.endsWith('kp')) return input.slice(-2);
  if (lower.endsWith('ny')) return input.slice(-2);
  const segments = [...new Intl.Segmenter('und', { granularity: 'grapheme' }).segment(input)];
  return segments[segments.length - 1]?.segment ?? '';
}

/** The answer for the image on screen, or null when it has no expected character. */
export function validatedAnswer(images: readonly SessionImage[], index: number, selected: string): LabelAnswer | null {
  const expected = images[index]?.character;
  if (!expected) return null;
  return { imageIndex: index, userAnswer: selected, expectedCharacter: expected, isCorrect: selected === expected };
}

/** A skipped image is sent as an empty, wrong answer. */
export function skippedAnswer(images: readonly SessionImage[], index: number): LabelAnswer {
  return { imageIndex: index, userAnswer: '', expectedCharacter: images[index]?.character ?? '', isCorrect: false };
}

/** Images in the session; before the first batch, the number asked for. */
export function sessionTotal(images: readonly SessionImage[]): number {
  return images.length || TOTAL_IMAGES;
}

/** Share of the session answered, 0..100 (not rounded, as before). */
export function sessionProgress(answers: readonly boolean[], total: number): number {
  return total > 0 ? (answers.length / total) * 100 : 0;
}

export function correctCount(answers: readonly boolean[]): number {
  return answers.filter(Boolean).length;
}

/** Correct over answered so far, whole percent. */
export function runningAccuracy(answers: readonly boolean[]): number {
  return answers.length > 0 ? Math.round((correctCount(answers) / answers.length) * 100) : 0;
}

/** Correct over the whole session, whole percent (end screen). */
export function finalAccuracy(correct: number, total: number): number {
  return total > 0 ? Math.round((correct / total) * 100) : 0;
}

/** Step state of each image: answered right, wrong or skipped, current, to do. */
export function imageOutcome(answers: readonly boolean[], index: number): 'done' | 'rejected' | null {
  if (index >= answers.length) return null;
  return answers[index] ? 'done' : 'rejected';
}

/** Whether the session view is past its setup screen. */
export function isSessionStarted(state: LabelSessionState): boolean {
  return state === 'active' || state === 'completing' || state === 'completed';
}
